import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCatalogKey,
  ShopifyCatalogToCompanyLocationKey,
  ShopifyCatalogToMarketKey,
  ShopifyCatalogToPriceListKey,
  ShopifyCatalogToShopKey,
  ShopifyCompanyLocationCatalogKey,
  ShopifyCompanyLocationCatalogToCatalogKey,
  ShopifyCompanyLocationCatalogToCompanyLocationKey,
  ShopifyCompanyLocationKey,
  ShopifyCompanyLocationToCatalogKey,
  ShopifyMarketCatalogKey,
  ShopifyMarketCatalogToCatalogKey,
  ShopifyMarketCatalogToMarketKey,
  ShopifyMarketKey,
  ShopifyMarketToCatalogKey,
  ShopifyPriceListKey,
  ShopifyPriceListToCatalogKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import { buildConnectionSpec, ConnectionModelSpecVersion } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCatalogKey,
  "2024-04": {
    name: "Shopify Catalog",
    graphQLTypename: "Catalog",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "id", graphQLFragments: ["AppCatalog", "CompanyLocationCatalog", "MarketCatalog"] },
        },
      },
      {
        name: "Type",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "AppCatalog" }, { name: "CompanyLocationCatalog" }, { name: "MarketCatalog" }],
        },
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "__typename", isShopifyEnum: true } },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "ACTIVE" }, { name: "ARCHIVED" }, { name: "DRAFT" }],
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFragments: ["AppCatalog", "CompanyLocationCatalog", "MarketCatalog"],
            graphQLFieldKey: "status",
            isShopifyEnum: true,
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "title",
            graphQLFragments: ["AppCatalog", "CompanyLocationCatalog", "MarketCatalog"],
          },
        },
      },
      {
        name: "Company Locations",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCatalogToCompanyLocationKey,
        configuration: {
          joinModelKey: ShopifyCompanyLocationCatalogKey,
          relatedModelKey: ShopifyCompanyLocationKey,
          inverseJoinModelFieldKey: ShopifyCompanyLocationCatalogToCatalogKey,
          inverseRelatedModelFieldKey: ShopifyCompanyLocationCatalogToCompanyLocationKey,
          inverseFieldKey: ShopifyCompanyLocationToCatalogKey,
        },
      },
      {
        name: "Markets",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCatalogToMarketKey,
        configuration: {
          joinModelKey: ShopifyMarketCatalogKey,
          relatedModelKey: ShopifyMarketKey,
          inverseJoinModelFieldKey: ShopifyMarketCatalogToCatalogKey,
          inverseRelatedModelFieldKey: ShopifyMarketCatalogToMarketKey,
          inverseFieldKey: ShopifyMarketToCatalogKey,
        },
      },
      {
        name: "Company Locations Count",
        fieldType: FieldType.JSON,
        shouldConvergeField: (enabledModelKeys) =>
          enabledModelKeys.some((key) => [ShopifyCompanyLocationCatalogKey, ShopifyCompanyLocationKey].includes(key)),
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFragments: ["CompanyLocationCatalog"],
            graphQLFieldKey: "companyLocationsCount",
            graphQLSelection: `
            count
            precision`,
          },
        },
      },
      {
        name: "Price List",
        fieldType: FieldType.HasOne,
        key: ShopifyCatalogToPriceListKey,
        configuration: {
          relatedModelKey: ShopifyPriceListKey,
          inverseFieldKey: ShopifyPriceListToCatalogKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCatalogToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "catalogs",
      filters: {},
    },
  },
};

export const ShopifyCatalog = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
